import * as React from "react";
import styled, { css } from "styled-components";
import Box from "@sproutsocial/seeds-react-box";

import type { TypeIconProps, TypeToggleProps } from "./IconTypes";
import IconHybrid from "./IconHybrid";

/**
 * Icon. Automatically routes between the Tailwind implementation (preferred, for
 * bare icons) and the styled-components implementation (for consumers using
 * `color` tokens, system props, or a styled() extension). The routing logic and
 * shared name/variant/aria/svg handling live in IconHybrid / iconShared.
 */
const Icon = (props: TypeIconProps) => <IconHybrid {...props} />;

const ToggleableIcon = styled(Icon)<{ active: boolean }>`
  transition: all ${(p) => p.theme.duration.fast} linear;

  ${(p) =>
    p.active &&
    css`
      opacity: 1;
      transform: scale(1);
    `}

  ${(p) =>
    !p.active &&
    css`
      position: absolute;
      opacity: 0;
      transform: scale(0);
    `}
`;

const IconToggle = ({
  activeName,
  inactiveName,
  isActive,
  size = "small",
  fixedWidth = false,
  ariaLabel,
  ...rest
}: TypeToggleProps) => {
  return (
    <Box as="span" position="relative" display="inline-flex" {...rest}>
      <ToggleableIcon
        active={isActive}
        name={activeName}
        size={size}
        fixedWidth={fixedWidth}
        aria-hidden={!isActive}
        aria-label={!isActive ? undefined : ariaLabel}
      />
      <ToggleableIcon
        active={!isActive}
        name={inactiveName}
        size={size}
        fixedWidth={fixedWidth}
        aria-hidden={isActive}
        aria-label={isActive ? ariaLabel : undefined}
      />
    </Box>
  );
};

IconToggle.displayName = "Icon.Toggle";
/**
 * **Accessibility note:** It is best practice to wrap `<Icon.Toggle />` in a button. The button must include `aria-label` and `aria-pressed` in order for a screen reader to properly communicate the icon's state. See example below.
 *
 * @link https://www.w3.org/TR/wai-aria-practices-1.1/#button
 * @example
 * const [toggleState, setToggleState] = useState(false);
 * <Button  // Wrap Icon.Toggle with Button
 *  appearance='pill'
 *  aria-label='like' // required for accessibility
 *  aria-pressed={toggleState} // required for accessibility
 *  onClick={() => setToggleState(!toggleState)}
 * >
 *  <Icon.Toggle
 *    activeName="heart-solid"
 *    inactiveName="heart-outline"
 *    isActive={toggleState}
 *   />
 * </Button>
 */
Icon.Toggle = IconToggle;

export default Icon;
